// Task screenshots: paste (Ctrl/⌘+V anywhere in the drawer), drop or pick images; thumbnails + full-size viewer.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Trash2, X } from "lucide-react";
import api from "../../../lib/api";

function Thumb({ taskId, att, onOpen, onDelete }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/workspace/tasks/${taskId}/images/${att.id}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => setSrc(""));
    return () => url && URL.revokeObjectURL(url);
  }, [taskId, att.id]);
  return (
    <div className="relative group rounded-lg border border-white/10 bg-black/30 overflow-hidden aspect-video" data-testid={`task-image-${att.id}`}>
      {src ? <img src={src} alt={att.name} className="w-full h-full object-cover cursor-zoom-in" onClick={() => onOpen(src, att)} />
        : <div className="w-full h-full grid place-items-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /></div>}
      <button type="button" onClick={() => onDelete(att)} title="Remove" data-testid={`task-image-delete-${att.id}`}
        className="absolute top-1 right-1 p-1 rounded bg-black/60 text-slate-300 hover:text-rose-300 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3" /></button>
      <div className="absolute bottom-0 inset-x-0 px-1.5 py-0.5 bg-black/60 text-[9.5px] text-slate-300 truncate">{att.by_name || ""} · {new Date(att.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</div>
    </div>
  );
}

export function useImagePaste(taskId, onUploaded) {
  const [busy, setBusy] = useState(false);
  const upload = useCallback(async (files) => {
    const imgs = [...files].filter((f) => f.type.startsWith("image/"));
    if (!imgs.length || !taskId) return false;
    setBusy(true);
    try {
      for (const f of imgs) {
        const fd = new FormData(); fd.append("file", f, f.name || `screenshot-${Date.now()}.png`);
        const { data } = await api.post(`/workspace/tasks/${taskId}/images`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        onUploaded?.(data.attachment);
      }
      toast.success(imgs.length > 1 ? `${imgs.length} images added` : "Screenshot added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
    return true;
  }, [taskId, onUploaded]);
  const onPaste = useCallback((e) => {
    const files = [...(e.clipboardData?.items || [])].filter((i) => i.kind === "file" && i.type.startsWith("image/")).map((i) => i.getAsFile()).filter(Boolean);
    if (files.length) { e.preventDefault(); upload(files); }
  }, [upload]);
  return { busy, upload, onPaste };
}

export default function TaskImages({ taskId, attachments = [], busy, upload, onRemoved }) {
  const [view, setView] = useState(null);
  const pick = useRef(null);
  const imgs = attachments.filter((a) => a.kind === "image");
  const del = async (att) => {
    if (!window.confirm(`Remove ${att.name}?`)) return;
    try { await api.delete(`/workspace/tasks/${taskId}/images/${att.id}`); onRemoved?.(att.id); toast.success("Image removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };
  return (
    <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }} data-testid="task-images">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11.5px] text-slate-400">Paste a screenshot (Ctrl/⌘ + V), drop or</span>
        <button type="button" onClick={() => pick.current?.click()} className="ws-chip" data-testid="task-image-pick"><ImagePlus className="w-3 h-3" />Add image</button>
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        <input ref={pick} type="file" accept="image/*" multiple hidden onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="task-image-input" />
      </div>
      {imgs.length === 0 ? <div className="rounded-lg border border-dashed border-white/15 py-5 text-center text-[11.5px] text-slate-500">No screenshots yet</div>
        : <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">{imgs.map((a) => <Thumb key={a.id} taskId={taskId} att={a} onOpen={(src, att) => setView({ src, att })} onDelete={del} />)}</div>}
      {view && (
        <div className="fixed inset-0 z-[90] bg-black/85 grid place-items-center p-6" onClick={() => setView(null)} data-testid="task-image-viewer">
          <img src={view.src} alt={view.att.name} className="max-w-full max-h-full rounded-lg shadow-2xl" />
          <button className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white" data-testid="task-image-viewer-close"><X className="w-5 h-5" /></button>
        </div>
      )}
    </div>
  );
}
